/**
 * SunoTimestampedJobsContext — фоновая операция Lyrics+ + кэш строк.
 *
 * По клику «Lyrics+» в ActionsSheet:
 *   1. Тост «Привязываем текст к таймкодам… 1-2 минуты»
 *   2. Шторка сама закрывается — юзер свободен
 *   3. Polling в фоне (персистентно, переживает reload через localStorage)
 *   4. По готовности: тост «Готов» + автооткрытие karaoke-viewer
 *
 * Кэш — JSON строк (обычно 5-50 KB на трек) в localStorage.
 * При повторном клике на трек — сразу открывается viewer без повторной генерации.
 */
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ReactNode,
} from 'react'
import { getTimestampedLyrics, getTrackTimestamped, type SunoTrack } from '../api/music'
import { waitForTask } from '../pages/generate/utils/waitForTask'
import { showToast } from '../components/Toast'
import { translateSunoError } from '../pages/generate/utils/sunoErrors'
import { providerOf } from '../pages/generate/utils/trackOperations'
import {
	extractLinesFromTask,
	groupWordsIntoLines,
	type AlignedWord,
	type LyricLine,
} from '../pages/generate/utils/sunoTimestamped'

const LS_PENDING_KEY = 'suno_timestamped_pending_v1'
const POLL_INTERVAL_MS = 10_000

interface PendingJob {
	trackId: string
	requestId: string
	title: string | null
	startedAt: number
}

interface CachedLines {
	trackId: string
	title: string | null
	lines: LyricLine[]
	createdAt: number
}

export interface TimestampedViewerState {
	trackId: string
	title: string | null
	lines: LyricLine[]
	audioUrl: string
	imageUrl: string | null
}

interface SunoTimestampedJobsValue {
	start: (track: SunoTrack) => void
	isRunning: (trackId: string) => boolean
	getCached: (trackId: string) => LyricLine[] | null
	pendingViewer: TimestampedViewerState | null
	dismissViewer: () => void
	openCached: (track: SunoTrack) => boolean
	/** Открыть viewer, подтянув aligned_words из БД если нет в локальном кэше.
	 *  Используется по клику на LRC-бейдж в SunoTrackList — flag has_timestamped
	 *  из /tracks подтверждает что данные есть на бэке. */
	openFromBackend: (track: SunoTrack) => Promise<boolean>
}

const SunoTimestampedJobsContext = createContext<SunoTimestampedJobsValue | null>(null)

// ─── localStorage helpers (ТОЛЬКО pending-jobs, не данные) ─────────
function loadPending(): Record<string, PendingJob> {
	try {
		const raw = localStorage.getItem(LS_PENDING_KEY)
		if (!raw) return {}
		const parsed = JSON.parse(raw)
		if (!parsed || typeof parsed !== 'object') return {}
		const filtered: Record<string, PendingJob> = {}
		for (const [k, v] of Object.entries(parsed as Record<string, PendingJob>)) {
			if (v && typeof v === 'object' && v.requestId && v.startedAt) {
				// Запуск с номером задачи живёт, пока сервер не ответил (план «Ждём ответа нейросети», Ф3): прежние
				// 30 минут бросали следить за долгой задачей после перезагрузки.
				filtered[k] = v
			}
		}
		return filtered
	} catch { return {} }
}
function savePending(pending: Record<string, PendingJob>) {
	try { localStorage.setItem(LS_PENDING_KEY, JSON.stringify(pending)) } catch { /* приватный режим / переполнение — не критично */ }
}

export function SunoTimestampedJobsProvider({ children }: { children: ReactNode }) {
	// In-memory session cache. Источник истины для has_timestamped — backend
	// (/tracks возвращает флаг, /tracks/:id/timestamped — сами aligned_words).
	const [cache, setCache] = useState<Record<string, CachedLines>>({})
	const [runningIds, setRunningIds] = useState<Set<string>>(
		() => new Set(Object.keys(loadPending())),
	)
	const [pendingViewer, setPendingViewer] = useState<TimestampedViewerState | null>(null)
	const activeRef = useRef<Set<string>>(new Set())
	const pendingRef = useRef<Record<string, PendingJob>>(loadPending())
	// Track info для restore после reload — title/audio_url могут потребоваться
	// для karaoke-viewer, поэтому храним на уровне ref когда start() вызвали.
	const trackRef = useRef<Record<string, SunoTrack>>({})

	const writeCache = useCallback(
		(trackId: string, title: string | null, lines: LyricLine[]) => {
			setCache((prev) => ({
				...prev,
				[trackId]: { trackId, title, lines, createdAt: Date.now() },
			}))
		},
		[],
	)

	const markRunning = useCallback((trackId: string, running: boolean) => {
		setRunningIds((prev) => {
			const next = new Set(prev)
			if (running) next.add(trackId)
			else next.delete(trackId)
			return next
		})
	}, [])

	const setPendingJob = useCallback((trackId: string, job: PendingJob | null) => {
		if (job) pendingRef.current[trackId] = job
		else delete pendingRef.current[trackId]
		savePending({ ...pendingRef.current })
	}, [])

	const publishViewer = useCallback((state: TimestampedViewerState | null) => {
		setPendingViewer(state)
	}, [])

	const runPolling = useCallback(
		async (trackId: string, requestId: string, track: SunoTrack | null) => {
			if (activeRef.current.has(trackId)) return
			activeRef.current.add(trackId)
			markRunning(trackId, true)
			setPendingJob(trackId, {
				trackId,
				requestId,
				title: track?.title ?? null,
				startedAt: pendingRef.current[trackId]?.startedAt ?? Date.now(),
			})

			try {
				// Ждём ответа сервера без потолка (waitForTask, план «Ждём ответа нейросети», Ф3).
				const s = await waitForTask(requestId, {
					minDelayMs: POLL_INTERVAL_MS,
					startedAt: pendingRef.current[trackId]?.startedAt,
				})
				if (!s) return
				if (s.status === 'failed') {
					showToast(translateSunoError(s.error_message_ru || s.error_message, 'timestamped'), 'error')
					return
				}
				const lines = extractLinesFromTask(s)
				if (!lines || lines.length === 0) {
					showToast('Suno завершил задачу без текста с таймкодами — попробуй ещё раз.', 'error')
					return
				}

				writeCache(trackId, track?.title ?? null, lines)
				showToast(`Текст с таймкодами готов — «${track?.title?.trim() || 'трек'}»`, 'success')
				if (track) {
					publishViewer({
						trackId,
						title: track.title,
						lines,
						audioUrl: track.audio_url || track.stream_audio_url || '',
						imageUrl: track.image_url || null,
					})
				}
				// /tracks теперь должен возвращать has_timestamped=true.
				// Уведомляем подписчиков → бейдж LRC появится на строке.
				window.dispatchEvent(
					new CustomEvent('suno-timestamped-ready', { detail: { trackId } }),
				)
			} finally {
				activeRef.current.delete(trackId)
				markRunning(trackId, false)
				setPendingJob(trackId, null)
			}
		},
		[markRunning, publishViewer, setPendingJob, writeCache],
	)

	const start = useCallback(
		async (track: SunoTrack) => {
			const trackId = track.id
			trackRef.current[trackId] = track

			// Cache hit — сразу открываем viewer.
			const cached = cache[trackId]
			if (cached) {
				publishViewer({
					trackId,
					title: track.title,
					lines: cached.lines,
					audioUrl: track.audio_url || track.stream_audio_url || '',
					imageUrl: track.image_url || null,
				})
				return
			}

			if (activeRef.current.has(trackId)) {
				showToast('Привязка текста уже идёт…', 'info')
				return
			}

			// Mureka отдаёт пословные тайминги вместе с самой генерацией, и бэк
			// собирает их из сохранённого ответа синхронно — ждать нечего.
			// Поэтому ни «1-2 минуты», ни polling: сразу открываем вьюер.
			if (providerOf(track.model) === 'mureka') {
				const instant = await getTimestampedLyrics({ track_id: trackId })
				if (!instant.ok) {
					showToast(translateSunoError(instant.error, 'timestamped'), 'error')
					return
				}
				const ready = await getTrackTimestamped(trackId)
				const words = Array.isArray(ready.aligned_words) ? (ready.aligned_words as AlignedWord[]) : []
				const lines = words.length > 0 ? groupWordsIntoLines(words) : []
				if (lines.length === 0) {
					showToast('У этого трека нет пословных таймингов', 'error')
					return
				}
				writeCache(trackId, track.title, lines)
				publishViewer({
					trackId,
					title: track.title,
					lines,
					audioUrl: track.audio_url || track.stream_audio_url || '',
					imageUrl: track.image_url || null,
				})
				// Тот же сигнал, что и у Suno-пути: список перезапрашивает /tracks
				// и на строке появляется значок караоке. Без него оно всплывало
				// бы только после перезагрузки страницы.
				window.dispatchEvent(
					new CustomEvent('suno-timestamped-ready', { detail: { trackId } }),
				)
				return
			}

			showToast(
				`Привязываем текст к таймкодам для «${track.title?.trim() || 'трека'}»… 1-2 минуты, можешь свернуть окно.`,
				'info',
			)

			const res = await getTimestampedLyrics({ track_id: trackId })
			if (!res.ok) {
				showToast(translateSunoError(res.error, 'timestamped'), 'error')
				return
			}
			if (!res.request_id) {
				showToast('Не удалось запустить привязку текста', 'error')
				return
			}
			runPolling(trackId, res.request_id, track)
		},
		[cache, publishViewer, runPolling, writeCache],
	)

	const openCached = useCallback(
		(track: SunoTrack): boolean => {
			const cached = cache[track.id]
			if (!cached) return false
			publishViewer({
				trackId: track.id,
				title: track.title,
				lines: cached.lines,
				audioUrl: track.audio_url || track.stream_audio_url || '',
				imageUrl: track.image_url || null,
			})
			return true
		},
		[cache, publishViewer],
	)

	const openFromBackend = useCallback(
		async (track: SunoTrack): Promise<boolean> => {
			if (openCached(track)) return true
			try {
				const res = await getTrackTimestamped(track.id)
				if (!res.ok || !Array.isArray(res.aligned_words) || res.aligned_words.length === 0) {
					return false
				}
				const lines = groupWordsIntoLines(res.aligned_words as AlignedWord[])
				if (lines.length === 0) return false
				publishViewer({
					trackId: track.id,
					title: track.title,
					lines,
					audioUrl: track.audio_url || track.stream_audio_url || '',
					imageUrl: track.image_url || null,
				})
				// Кэш для быстрого повторного открытия
				writeCache(track.id, track.title, lines)
				return true
			} catch {
				return false
			}
		},
		[openCached, publishViewer, writeCache],
	)

	const getCached = useCallback(
		(trackId: string): LyricLine[] | null => cache[trackId]?.lines ?? null,
		[cache],
	)

	const dismissViewer = useCallback(() => {
		publishViewer(null)
	}, [publishViewer])

	// Restore pending после reload. track data не перезапишется — polling
	// всё равно положит результат в cache, viewer откроется когда юзер вернётся.
	useEffect(() => {
		const pending = pendingRef.current
		const ids = Object.keys(pending)
		if (ids.length === 0) return
		const t = setTimeout(() => {
			for (const trackId of ids) {
				const job = pending[trackId]
				if (!job) continue
				const trk = trackRef.current[trackId] ?? null
				runPolling(trackId, job.requestId, trk)
			}
		}, 500)
		return () => clearTimeout(t)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const isRunning = useCallback((trackId: string) => runningIds.has(trackId), [runningIds])

	const value = useMemo<SunoTimestampedJobsValue>(
		() => ({ start, isRunning, getCached, pendingViewer, dismissViewer, openCached, openFromBackend }),
		[start, isRunning, getCached, pendingViewer, dismissViewer, openCached, openFromBackend],
	)

	return (
		<SunoTimestampedJobsContext.Provider value={value}>
			{children}
		</SunoTimestampedJobsContext.Provider>
	)
}

// Хук живёт рядом с провайдером — как во всех наших контекстах; выносить его
// в отдельный файл ради fast-refresh не станем (это сломало бы импорты по всему
// проекту), поэтому правило глушим точечно.
// eslint-disable-next-line react-refresh/only-export-components
export function useSunoTimestampedJobs(): SunoTimestampedJobsValue {
	const ctx = useContext(SunoTimestampedJobsContext)
	if (!ctx) {
		return {
			start: () => {},
			isRunning: () => false,
			getCached: () => null,
			pendingViewer: null,
			dismissViewer: () => {},
			openCached: () => false,
			openFromBackend: async () => false,
		}
	}
	return ctx
}
